import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import useTimerWS from "@/hooks/useTimerWS";
import useAdaptivePolling from "@/hooks/useAdaptivePolling";
import { useEvent } from "@/components/EventLayout";
import TimerDisplay from "@/components/timer/TimerDisplay";
import { ss, offsetTransform } from "@/utils/displayStyle";
import { requestFullscreen, exitFullscreen, getFullscreenElement, onFullscreenChange } from "@/utils/fullscreen";
import ReloadButton from "@/components/ReloadButton";
import usePwaManifest from "@/hooks/usePwaManifest";

import { BACKEND_URL } from '../utils/apiBase';
const API = `${BACKEND_URL}/api`;
const getImageUrl = (url) => (!url ? "" : url.startsWith("https://res.cloudinary.com/") ? url : `${BACKEND_URL}${url}`);

/**
 * Second public display, dedicated to the countdown / timer only.
 * URL: /event/{slug}/countdown
 *
 * Shows (top → bottom):
 *   • Stage label ("TOP 32", "Quarter Final", "Circle Qualification"…)
 *   • Circle number ("Circle #2" — only when a circle is active)
 *   • Round label ("Round 1 / 3")
 *   • Huge timer number
 *
 * Background = same theme (image / gradient) as the main Big Screen.
 * When no battle and no circle are active → background only, nothing else.
 *
 * Fully synchronized with the rest via the same `useTimerWS` + polling
 * stack as the main DisplayPage.
 */
const ROUND_NAMES = {
  TOP32: "TOP 32",
  TOP16: "TOP 16",
  QUARTER_FINALS: "QUARTER FINALS",
  SEMI_FINALS: "SEMI FINALS",
  THIRD_PLACE: "THIRD PLACE",
  FINAL: "FINAL",
};

const CountdownDisplayPage = () => {
  usePwaManifest('/manifest-countdown.json');
  const { theme } = useTheme();
  const { t } = useI18n();
  const event = useEvent();
  const { battleTimers, circleTimers, refreshBattle, refreshCircle } = useTimerWS(event?.event_id);

  const [currentBattle, setCurrentBattle] = useState(null);
  const [currentCircle, setCurrentCircle] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef(null);
  const fpRef = useRef({ bId: null, bSt: null, cId: null, cSt: null });

  // Persistent fullscreen intent: browsers (Chrome especially) exit
  // fullscreen when the user opens a new tab. We remember the intent in
  // localStorage and quietly re-enter fullscreen on the next user click
  // (which is the minimum browser requirement — any user gesture will do,
  // no confirmation needed). Invisible from the user's POV.
  const FS_KEY = "countdown_fs_intent";
  const wantsFs = useRef(false);
  useEffect(() => {
    try { wantsFs.current = localStorage.getItem(FS_KEY) === "1"; } catch { /* no localStorage */ }
  }, []);
  const toggleFullscreen = () => {
    const el = rootRef.current || document.documentElement;
    if (!getFullscreenElement()) {
      wantsFs.current = true;
      try { localStorage.setItem(FS_KEY, "1"); } catch { /* ignore */ }
      requestFullscreen(el).then(() => setIsFullscreen(!!getFullscreenElement()));
    } else {
      wantsFs.current = false;
      try { localStorage.removeItem(FS_KEY); } catch { /* ignore */ }
      exitFullscreen().then(() => setIsFullscreen(!!getFullscreenElement()));
    }
  };
  useEffect(() => onFullscreenChange(() => setIsFullscreen(!!getFullscreenElement())), []);
  // Auto re-enter fullscreen on next click if the intent is still set
  // AND the browser has since dropped us out (tab switch, new tab, ...).
  // Uses the click event as the required user gesture.
  useEffect(() => {
    const maybeRestore = () => {
      if (!wantsFs.current) return;
      if (getFullscreenElement()) return;
      const el = rootRef.current || document.documentElement;
      requestFullscreen(el).catch(() => { /* browser refused, silent */ });
    };
    document.addEventListener("click", maybeRestore, true);
    document.addEventListener("keydown", maybeRestore, true);
    document.addEventListener("touchstart", maybeRestore, true);
    return () => {
      document.removeEventListener("click", maybeRestore, true);
      document.removeEventListener("keydown", maybeRestore, true);
      document.removeEventListener("touchstart", maybeRestore, true);
    };
  }, []);
  // Also detect ESC exit → clear the intent so we don't fight the user.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !getFullscreenElement()) {
        wantsFs.current = false;
        try { localStorage.removeItem(FS_KEY); } catch { /* ignore */ }
      }
    };
    window.addEventListener("keyup", onKey);
    return () => window.removeEventListener("keyup", onKey);
  }, []);

  const loadCurrent = useCallback(async () => {
    try {
      const bRes = await axios.get(`${API}/battle/current`);
      if (bRes.data) {
        const { id: nId, status: nS } = bRes.data.battle || {};
        if (nId !== fpRef.current.bId || nS !== fpRef.current.bSt) {
          fpRef.current.bId = nId; fpRef.current.bSt = nS;
          setCurrentBattle(bRes.data);
        }
        // Battle takes priority; clear any lingering circle.
        if (fpRef.current.cId) { fpRef.current.cId = null; fpRef.current.cSt = null; setCurrentCircle(null); }
      } else {
        if (fpRef.current.bId) { fpRef.current.bId = null; fpRef.current.bSt = null; setCurrentBattle(null); }
        const cRes = await axios.get(`${API}/circle/current`);
        if (cRes.data) {
          const { id: cId, status: cS } = cRes.data.circle || {};
          if (cId !== fpRef.current.cId || cS !== fpRef.current.cSt) {
            fpRef.current.cId = cId; fpRef.current.cSt = cS;
            setCurrentCircle(cRes.data);
          }
        } else if (fpRef.current.cId) {
          fpRef.current.cId = null; fpRef.current.cSt = null; setCurrentCircle(null);
        }
      }
    } catch { /* keep last known */ }
  }, []);

  // Poll fast while an active event is running, slow otherwise (same rule
  // as JudgePage/DisplayPage — see iter 41 sync robustness).
  const battleTimer = currentBattle?.battle?.id ? battleTimers[currentBattle.battle.id] : null;
  const circleTimer = currentCircle?.circle?.id ? circleTimers[currentCircle.circle.id] : null;
  const liveTimerStatuses = ["pre_start", "pre_countdown", "running", "paused"];
  const isActive = (
    currentBattle?.battle?.status === "voting" ||
    currentBattle?.battle?.status === "revealing" ||
    currentCircle?.circle?.status === "voting" ||
    currentCircle?.circle?.status === "revealing" ||
    liveTimerStatuses.includes(battleTimer?.status) ||
    liveTimerStatuses.includes(circleTimer?.status)
  );
  const pollingFn = useCallback(async () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    await loadCurrent();
    const bId = fpRef.current.bId;
    const cId = fpRef.current.cId;
    if (bId) refreshBattle(bId);
    if (cId) refreshCircle(cId);
  }, [loadCurrent, refreshBattle, refreshCircle]);
  useEffect(() => { loadCurrent(); }, [loadCurrent]);
  useAdaptivePolling(pollingFn, isActive);

  // Refresh initial timer states on entity change.
  useEffect(() => { if (currentBattle?.battle?.id) refreshBattle(currentBattle.battle.id); }, [currentBattle?.battle?.id, refreshBattle]);
  useEffect(() => { if (currentCircle?.circle?.id) refreshCircle(currentCircle.circle.id); }, [currentCircle?.circle?.id, refreshCircle]);

  // Background — reuse the main Big Screen wallpaper.
  const bgStyle = {
    background: theme.background_image_url
      ? `url(${getImageUrl(theme.background_image_url)}) center/cover no-repeat fixed`
      : `linear-gradient(135deg, ${theme.gradient_start} 0%, ${theme.gradient_end} 100%)`,
  };

  // Determine what to render
  const hasActive = Boolean(currentBattle || currentCircle);
  const kind = currentBattle ? "battle" : (currentCircle ? "circle" : null);
  const activeTimer = kind === "battle" ? battleTimer : (kind === "circle" ? circleTimer : null);

  // Compute the labels — matches the requested minimalist design:
  //   "CIRCLE 1"  (no #, no total)
  //   "ROUND 1"   (no /N)
  //   big timer number
  let stageLabel = "";
  let circleLabel = "";
  let roundLabel = "";
  if (kind === "battle") {
    const battle = currentBattle.battle;
    const tour = currentBattle.tournament;
    if (tour?.type === "1vs1_battles" && tour?.name) {
      stageLabel = tour.name;
    } else if (battle?.round) {
      stageLabel = ROUND_NAMES[battle.round] || battle.round;
    }
    const current = activeTimer?.round || battle?.current_round || 1;
    roundLabel = `${(t("rounds.round") || "ROUND").toUpperCase()} ${current}`;
  } else if (kind === "circle") {
    const circle = currentCircle.circle;
    if (circle?.circle_number != null) {
      circleLabel = `${(t("circle.title") || "CIRCLE").toUpperCase()} ${circle.circle_number}`;
    }
    const seq = activeTimer?.sequence || [];
    const idx = activeTimer?.segment_index || 0;
    const currentRound = seq[idx]?.round || 1;
    roundLabel = `${(t("rounds.round") || "ROUND").toUpperCase()} ${currentRound}`;
  }

  // Per-element style
  const stageS = ss(theme, "countdown", "stage_label");
  const circleS = ss(theme, "countdown", "circle_label");
  const roundS = ss(theme, "countdown", "round_label");
  const showTimer = Boolean(activeTimer);

  return (
    <div
      ref={rootRef}
      className="h-screen w-screen overflow-hidden relative flex flex-col items-center justify-center"
      style={{...bgStyle, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.65), 0 0 32px rgba(0,0,0,0.35)'}}
      data-testid="countdown-display-root"
    >
      {!isFullscreen && (
        <button
          type="button"
          onClick={toggleFullscreen}
          className="fixed top-4 right-4 z-50 px-3 py-1.5 text-white rounded-lg font-medium"
          style={{ fontSize: 'clamp(0.65rem, 0.85vw, 0.9rem)', opacity: 0, background: 'rgba(128,90,213,0.8)', transition: 'opacity 0.3s' }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}
          data-testid="countdown-fullscreen-btn"
        >
          {t('display.fullscreen') || 'Fullscreen'}
        </button>
      )}
      {!hasActive ? null : (
        <div
          className="flex flex-col items-center justify-center select-none"
          style={{
            gap: 'clamp(0.25rem, 0.7vh, 0.75rem)',
            fontFamily: 'Inter, system-ui, sans-serif',
            textAlign: 'center',
          }}
          data-testid="countdown-display-content"
        >
          {stageLabel && (
            <div
              className="uppercase font-black tracking-tight"
              style={{
                color: stageS.color,
                fontSize: `${(stageS.size || 1) * 3.5}vw`,
                lineHeight: 0.95,
                letterSpacing: '0.02em',
                transform: offsetTransform(stageS),
                textShadow: stageS.textShadow,
              }}
              data-testid="countdown-stage-label"
            >
              {stageLabel}
            </div>
          )}
          {circleLabel && (
            <div
              className="uppercase font-black tracking-tight"
              style={{
                color: circleS.color,
                fontSize: `${(circleS.size || 1) * 4.5}vw`,
                lineHeight: 0.95,
                letterSpacing: '0.02em',
                transform: offsetTransform(circleS),
                textShadow: circleS.textShadow,
              }}
              data-testid="countdown-circle-label"
            >
              {circleLabel}
            </div>
          )}
          {roundLabel && (
            <div
              className="uppercase font-black tracking-tight"
              style={{
                color: roundS.color,
                fontSize: `${(roundS.size || 1) * 7}vw`,
                lineHeight: 0.95,
                letterSpacing: '0.02em',
                transform: offsetTransform(roundS),
                textShadow: roundS.textShadow,
              }}
              data-testid="countdown-round-label"
            >
              {roundLabel}
            </div>
          )}
          {showTimer && (
            <div
              className="mt-1"
              data-testid="countdown-timer-wrap"
            >
              <TimerDisplay
                timer={activeTimer}
                variant="huge"
                playSounds={false}
                showProgress={false}
                screen="countdown"
              />
            </div>
          )}
        </div>
      )}
      <ReloadButton />
    </div>
  );
};

export default CountdownDisplayPage;
